import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, Sparkles, Volume2, Wifi, Youtube, Instagram, Facebook } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { AppleIcon, GooglePlayIcon, TikTokIcon } from "./icons";

const SOCIALS = [
  { name: "YouTube", Icon: Youtube, href: "https://www.youtube.com/@lingomind-v01", id: "hero-social-youtube" },
  { name: "Instagram", Icon: Instagram, href: "https://www.instagram.com/lingomindapp/", id: "hero-social-instagram" },
  { name: "Facebook", Icon: Facebook, href: "https://www.facebook.com/profile.php?id=61593085982319", id: "hero-social-facebook" },
  { name: "TikTok", Icon: TikTokIcon, href: "https://www.tiktok.com/@lingomindapp", id: "hero-social-tiktok" },
];

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
});

const WORDS = [
  { front: "we", back: "wir", example: "we are friends → wir sind Freunde" },
  { front: "hello", back: "hola", example: "hello, how are you? → hola, ¿cómo estás?" },
  { front: "thank you", back: "merci", example: "thank you very much → merci beaucoup" },
  { front: "water", back: "su", example: "I drink water → su içiyorum" },
  { front: "friend", back: "amico", example: "my friend → il mio amico" },
  { front: "good morning", back: "おはよう", example: "good morning! → おはようございます" },
];

const Hero = () => {
  const { t, source } = useLanguage();
  const [flipped, setFlipped] = useState(false);
  const [wordIdx, setWordIdx] = useState(0);
  const interacted = useRef(false);
  const flippedRef = useRef(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      if (interacted.current) return;
      if (flippedRef.current) {
        setWordIdx((i) => (i + 1) % WORDS.length);
        setFlipped(false);
        flippedRef.current = false;
      } else {
        setFlipped(true);
        flippedRef.current = true;
      }
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const flipCard = () => {
    interacted.current = true;
    setFlipped((f) => {
      flippedRef.current = !f;
      return !f;
    });
  };

  const word = WORDS[wordIdx];
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 16 });

  const onMouseMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section className="relative overflow-hidden pb-20 pt-32 sm:pt-40">
      <div className="pointer-events-none absolute -top-32 end-[-10%] h-[480px] w-[480px] rounded-full bg-sky-200/50 blur-3xl" />
      <div className="pointer-events-none absolute top-40 start-[-12%] h-[380px] w-[380px] rounded-full bg-amber-200/40 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-amber-300/60 bg-amber-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-amber-700"
            data-testid="hero-badge"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t.hero.badge}
          </motion.div>

          <h1 className="font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(0)}>
                {t.hero.title1}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block bg-gradient-to-r from-sky-600 via-sky-500 to-amber-500 bg-clip-text text-transparent"
                {...lineAnim(1)}
              >
                {t.hero.title2}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-slate-400" {...lineAnim(2)}>
                {t.hero.title3}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-description"
          >
            {t.hero.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-google-play-button"
              href="https://play.google.com/store/apps/details?id=com.lingomind.app&hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-all hover:-translate-y-0.5 hover:drop-shadow-2xl"
            >
              <img src="/google-play-badge.png" alt={t.hero.googlePlay} className="h-14 w-auto" />
            </a>
            <button
              data-testid="hero-app-store-button"
              onClick={() => toast.info(t.hero.appStoreSoon)}
              className="flex items-center gap-3 rounded-2xl bg-slate-900 px-6 py-3 text-white shadow-xl shadow-slate-900/20 transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-sky-900/25"
            >
              <AppleIcon className="h-7 w-7" />
              <span className="text-start">
                <span className="block text-sm font-bold leading-tight">{t.hero.appStoreCta}</span>
              </span>
            </button>
          </motion.div>

          {source === "geo" && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="mt-5 flex items-center gap-2 text-xs font-medium text-slate-500"
              data-testid="hero-auto-detect-note"
            >
              <BadgeCheck className="h-4 w-4 text-sky-500" />
              {t.hero.autoDetected}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-7 flex items-center gap-3"
            data-testid="hero-social-row"
          >
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
              {t.footer.connect}
            </span>
            <span className="h-px w-8 bg-gradient-to-r from-amber-400 to-transparent" />
            {SOCIALS.map(({ name, Icon, href, id }) => (
              <a
                key={name}
                data-testid={id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={name}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-sky-200 bg-white text-slate-500 shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-400 hover:text-amber-600 hover:shadow-lg hover:shadow-amber-500/15"
              >
                <Icon className="h-4 w-4" />
                <span className="sr-only">{name}</span>
              </a>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-12 grid max-w-xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-sky-100 bg-sky-100 sm:grid-cols-4"
            data-testid="hero-stats"
          >
            {t.hero.stats.map((s) => (
              <div key={s.label} className="bg-white px-4 py-5 text-center">
                <div className="font-heading text-2xl font-extrabold text-sky-700">{s.value}</div>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden justify-center lg:flex"
          style={{ perspective: 1200 }}
        >
          <motion.div
            ref={cardRef}
            onMouseMove={onMouseMove}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative w-[300px] rounded-[2.5rem] border border-sky-100 bg-white p-5 shadow-2xl shadow-sky-900/15"
            data-testid="hero-phone-mock"
          >
            <div className="mb-5 flex items-center justify-between">
              <img src="/logo-app.png" alt="" className="h-9 w-9 rounded-xl" />
              <div className="flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1 text-[10px] font-bold text-sky-700">
                <Wifi className="h-3 w-3" /> 100%
              </div>
            </div>
            <button
              data-testid="hero-flashcard"
              onClick={flipCard}
              className="block w-full cursor-pointer"
              style={{ perspective: 900 }}
              aria-label={t.hero.cardFrontHint}
            >
              <motion.div
                animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformStyle: "preserve-3d" }}
                className="relative"
              >
                <div className="backface-hidden rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-6 text-center">
                  <div className="font-heading text-4xl font-extrabold text-slate-900">{word.front}</div>
                  <div className="mt-3 text-[11px] font-medium uppercase tracking-widest text-slate-400">
                    {t.hero.cardFrontHint}
                  </div>
                </div>
                <div
                  className="backface-hidden absolute inset-0 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 text-center"
                  style={{ transform: "rotateY(180deg)" }}
                >
                  <div className="flex items-center justify-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 shadow-lg shadow-amber-500/30">
                      <Volume2 className="h-4 w-4 text-white" />
                    </span>
                    <span className="font-heading text-4xl font-extrabold text-sky-600">{word.back}</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    {word.example.split("→")[0]}→ <span className="font-semibold text-slate-700">{word.example.split("→")[1]}</span>
                  </div>
                </div>
              </motion.div>
            </button>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-slate-200 py-2.5 text-center text-xs font-bold text-slate-500">
                {t.hero.cardKeep}
              </div>
              <div className="rounded-xl bg-sky-600 py-2.5 text-center text-xs font-bold text-white">
                {t.hero.cardGotIt}
              </div>
            </div>
            <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-sky-100">
              <motion.div
                initial={{ width: "8%" }}
                animate={{ width: "64%" }}
                transition={{ duration: 2.4, delay: 1.4, ease: "easeOut" }}
                className="h-full rounded-full bg-gradient-to-r from-sky-500 to-amber-400"
              />
            </div>
          </motion.div>
          <div className="absolute -bottom-8 -z-10 h-24 w-64 rounded-full bg-sky-300/30 blur-2xl" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
